import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Download, MoreHorizontal, Building2 } from 'lucide-react';
import { useCompanies } from '@/composables/crm/useCompanies';
import { useExport } from '@/composables/shared/useExport';
import { useDebounce } from '@/composables/shared/useDebounce';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatDate, formatCurrency, formatNumber } from '@/lib/formatters';
import { useState, useMemo } from 'react';
import type { Company } from '@/types/table';

export function CompanyListPage() {
  const navigate = useNavigate();
  const { companies, meta, isLoading, filters, exportCsv, deleteCompany } = useCompanies();
  const { exportFile, isExporting } = useExport();
  const [search, setSearch] = useState(filters.current.search);
  const debouncedSearch = useDebounce(search, 300);
  if (debouncedSearch !== filters.current.search) filters.set({ search: debouncedSearch });

  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);

  const columns: ColumnDef<Company, unknown>[] = useMemo(() => [
    { accessorKey: 'name', header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />, cell: ({ row }) => <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{row.original.name}</span></div> },
    { accessorKey: 'industry', header: 'Industry', cell: ({ row }) => row.original.industry || '-' },
    { accessorKey: 'employeeCount', header: 'Employees', cell: ({ row }) => row.original.employeeCount ? formatNumber(row.original.employeeCount) : '-' },
    { accessorKey: 'annualRevenue', header: 'Revenue', cell: ({ row }) => row.original.annualRevenue ? formatCurrency(row.original.annualRevenue) : '-' },
    { accessorKey: 'createdAt', header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />, cell: ({ row }) => formatDate(row.original.createdAt) },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/companies/${row.original.id}`); }}>View</DropdownMenuItem>
            <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PageContainer title="Companies" description="Manage your companies" actions={<><Button variant="outline" onClick={() => exportFile(exportCsv, 'companies.csv')} disabled={isExporting}><Download className="mr-2 h-4 w-4" />Export</Button><Button onClick={() => navigate('/companies/new')}><Plus className="mr-2 h-4 w-4" />Add Company</Button></>}>
      <DataTableToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search companies..." />
      <DataTable columns={columns} data={companies} isLoading={isLoading} page={meta?.page ?? 1} totalPages={meta?.totalPages ?? 1} total={meta?.total ?? 0} pageSize={filters.current.limit} onPageChange={(p) => filters.set({ page: p })} onPageSizeChange={(s) => filters.set({ limit: s })} onRowClick={(row) => navigate(`/companies/${row.id}`)} />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Company"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { deleteCompany.mutate(deleteTarget.id); setDeleteTarget(null); } }}
        isLoading={deleteCompany.isPending}
      />
    </PageContainer>
  );
}
